import { z } from 'zod'
import type { LoggedUser } from '../../models/LoggedUser'
import { UserPermissionsGroupDTO, mapDTOToUserPermissionsGroup, mapUserPermissionsGroupToDTO } from '../../../permission-groups/gateways/DTOs/UserPermissionsGroupDTO'
import { BaseCompanyDTO, mapBaseCompanyToDTO, mapDTOToBaseCompany } from '~/domain/shared/company/gateways/DTOs/BaseCompanyDTO'

const UserPermissionsGroupDTORequestSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  global: z.boolean().nullish(),
  editable: z.boolean().nullish(),
  permissions: z.array(z.string()).nullish(),
  pdv_permissions: z.array(z.string()).nullish()
})

export const LoggedUserDTO = z.object({
  id: z.number().optional(),
  name: z.string(),
  email: z.string().nullish(),
  profile: z.union([UserPermissionsGroupDTO, UserPermissionsGroupDTORequestSchema]).optional(),
  companies: BaseCompanyDTO.array(),
  password: z.string().nullish(),
  current_password: z.string().nullish(),
  password_confirmation: z.string().nullish()
})

export type LoggedUserDTO = z.infer<typeof LoggedUserDTO>

export function mapLoggedUserToDTO (model: LoggedUser): LoggedUserDTO {
  const { password, currentPassword, passwordConfirmation, ...user } = model

  const dto: LoggedUserDTO = {
    id: user.id,
    name: user.name,
    email: user.email ?? '',
    profile: user.permissionGroup ? mapUserPermissionsGroupToDTO(user.permissionGroup) : undefined,
    companies: user.companies.map(mapBaseCompanyToDTO)
  }

  if (password) {
    dto.password = password
  }

  if (currentPassword) {
    dto.current_password = currentPassword
  }

  if (passwordConfirmation) {
    dto.password_confirmation = passwordConfirmation
  }

  return dto
}

export function mapDTOToLoggedUser (dto: LoggedUserDTO): LoggedUser {
  // Check if profile has the Request format (string[]) or DTO format (object[])
  const permissionGroup = dto.profile
    ? (dto.profile.permissions && typeof dto.profile.permissions[0] === 'string')
        ? undefined // If it's Request format, we can't map it back (would need API call)
        : mapDTOToUserPermissionsGroup(dto.profile as z.infer<typeof UserPermissionsGroupDTO>)
    : undefined

  return {
    id: dto.id,
    name: dto.name ?? '',
    email: dto.email ?? '',
    permissionGroup,
    companies: dto.companies.map(mapDTOToBaseCompany),
    addresses: [],
    documents: [],
    emails: dto.email != null ? [dto.email] : [],
    phones: []
  }
}
